import { IconBox, IconChevronRight, IconRefresh, IconX } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { ProductThumbnail } from "@/components/shared/product-thumbnail";
import { Button } from "@/components/ui/button";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { PaginationFooter, PaneHeading, ProductRowsSkeleton } from "./PickerParts";
import {
  cannotAddListing,
  displayedInventoryQuantity,
  type ProductInventoryGate,
  selectedProductFromListing,
} from "./product-listing-helpers";
import type { ProductListingBrowse } from "./use-product-listing-browse";
import type { ProductSelection } from "./use-product-selection";

interface SingleProductPanelProps {
  browse: ProductListingBrowse;
  selection: ProductSelection;
  adProduct?: "sp";
  inventory: ProductInventoryGate;
  /** "Change" keeps the current pick and sends the user back to the search box. */
  onReplace: () => void;
}

/**
 * Paper TP-01 single-SKU picker (template portfolio): product groups, SKU radio rows and a
 * "Selected product" card with Change / Remove. No tabs, select-all or group checkboxes.
 */
export function SingleProductPanel({
  browse,
  selection,
  adProduct,
  inventory,
  onReplace,
}: SingleProductPanelProps) {
  const { t } = useTranslation("campaignCreation");
  const { groups, listings, activeGroup, vendor } = browse;
  const selected = selection.selected[0] ?? null;
  const listingRows = listings.isPlaceholderData ? [] : (listings.data?.data ?? []);

  return (
    <div className="grid h-[300px] grid-cols-[minmax(250px,0.9fr)_minmax(360px,1.25fr)_minmax(280px,0.9fr)] overflow-hidden rounded-xl border border-line bg-surface">
      <section
        className="flex min-h-0 min-w-0 flex-col border-r border-line"
        aria-label={t("products.parentPane")}
      >
        <PaneHeading title={t("products.parentAsin")} />
        <div className="min-h-0 flex-1 overflow-y-auto paper-scrollbar">
          {groups.isError ? (
            <ErrorState message={t("products.loadError")} onRetry={() => void groups.refetch()} />
          ) : groups.isPending || groups.isPlaceholderData ? (
            <ProductRowsSkeleton label={t("products.loading")} />
          ) : groups.data?.data.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">{t("products.noMatches")}</p>
          ) : (
            groups.data?.data.map((group) => (
              <div
                key={group.variationGroupKey}
                className={cn(
                  "flex items-center gap-2.5 border-b border-line px-3 py-2 hover:bg-table-row-hover",
                  activeGroup === group.variationGroupKey && "bg-ink/7",
                )}
              >
                <ProductThumbnail
                  imageUrl={group.imageUrl}
                  label={group.title ?? group.variationGroupKey}
                  asin={group.parentAsin ?? group.singleListing?.asin}
                  sku={group.singleListing?.sku ?? undefined}
                  productStatus={group.singleListing?.productStatus}
                />
                <button
                  type="button"
                  aria-pressed={activeGroup === group.variationGroupKey}
                  onClick={() => browse.showGroup(group.variationGroupKey)}
                  className="flex min-w-0 flex-1 items-center gap-2.5 text-left outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate font-mono text-xs font-semibold text-ink">
                      {group.variationGroupKey}
                    </span>
                    <span className="truncate text-[11px] leading-[14px] text-muted-foreground">
                      {t("products.asinCount", { count: group.childAsinCount })}
                      {vendor
                        ? null
                        : ` · ${t("products.skuCount", { count: group.matchingSkuCount })}`}
                    </span>
                  </span>
                  <IconChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              </div>
            ))
          )}
        </div>
        <SinglePanePagination
          page={browse.groupPage}
          pagination={groups.data?.pagination}
          onPageChange={browse.setGroupPage}
        />
      </section>

      <section
        className="flex min-h-0 min-w-0 flex-col border-r border-line"
        aria-label={t(vendor ? "products.asinPane" : "products.skuPane")}
      >
        <PaneHeading title={t(vendor ? "products.asinColumn" : "products.sku")} />
        <div className="min-h-0 flex-1 overflow-y-auto paper-scrollbar">
          {listings.isError ? (
            <ErrorState message={t("products.loadError")} onRetry={() => void listings.refetch()} />
          ) : activeGroup && (listings.isPending || listings.isPlaceholderData) ? (
            <ProductRowsSkeleton label={t("products.loading")} />
          ) : !activeGroup || listingRows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">{t("products.noMatches")}</p>
          ) : null}
          <RadioGroup
            value={selected?.listingKey ?? null}
            onValueChange={(listingKey) => {
              const listing = listingRows.find((candidate) => candidate.listingKey === listingKey);
              if (listing) selection.addProducts([selectedProductFromListing(listing)], "search");
            }}
            aria-label={t("products.skuPane")}
            className="block"
          >
            {listingRows.map((listing) => {
              const checked = selected?.listingKey === listing.listingKey;
              const blocked = cannotAddListing(listing, adProduct, inventory);
              // Rows of a still-settling search may be replaced, so they are not pickable yet.
              const disabled = blocked || browse.searchSettling;
              const quantity = displayedInventoryQuantity(listing);
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: the Radio inside is the keyboard target.
                // biome-ignore lint/a11y/noStaticElementInteractions: row click mirrors the Radio.
                <div
                  key={listing.listingKey}
                  onClick={() => {
                    if (!disabled && !checked)
                      selection.addProducts([selectedProductFromListing(listing)], "search");
                  }}
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 border-b border-line px-3 py-2",
                    checked ? "bg-badge-information-background" : "hover:bg-table-row-hover",
                    disabled && "cursor-not-allowed",
                    blocked && "opacity-60",
                  )}
                >
                  <Radio
                    value={listing.listingKey}
                    disabled={disabled}
                    aria-label={
                      listing.sku
                        ? t("products.selectSku", { sku: listing.sku })
                        : t("products.selectAsin", { asin: listing.asin })
                    }
                    onClick={(event) => event.stopPropagation()}
                    className="size-4 border-control-border-hover bg-surface data-checked:border-[5px] data-checked:border-primary data-checked:bg-surface [&>[data-slot=radio-indicator]]:hidden"
                  />
                  <ProductThumbnail
                    imageUrl={listing.imageUrl}
                    label={listing.title ?? listing.asin}
                    asin={listing.asin}
                    sku={listing.sku ?? undefined}
                    productStatus={listing.productStatus}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="line-clamp-1 text-xs font-medium text-ink">
                      {listing.title ?? t("products.noTitle")}
                    </span>
                    {vendor ? null : (
                      <span className="flex gap-1">
                        {listing.fulfillmentChannel ? (
                          <SingleChip>
                            {t(`products.fulfillment.${listing.fulfillmentChannel}`)}
                          </SingleChip>
                        ) : null}
                        <SingleChip>
                          {quantity == null
                            ? t("products.inventoryUnknown")
                            : t("products.quantity", { count: quantity })}
                        </SingleChip>
                      </span>
                    )}
                    <span className="truncate font-mono text-[10px] leading-3 text-muted-foreground">
                      ASIN: {listing.asin}
                      {listing.sku && !vendor ? ` · SKU: ${listing.sku}` : ""}
                    </span>
                  </span>
                </div>
              );
            })}
          </RadioGroup>
        </div>
        <SinglePanePagination
          page={browse.listingPage}
          pagination={listings.data?.pagination}
          onPageChange={browse.setListingPage}
        />
      </section>

      <section
        className="flex min-h-0 min-w-0 flex-col overflow-y-auto bg-surface"
        aria-label={t("products.selected")}
      >
        <PaneHeading title={t("products.selected")} />
        {selected ? (
          <div className="flex flex-col gap-3 p-3">
            <div className="flex items-center gap-2.5">
              <ProductThumbnail
                imageUrl={selected.imageUrl}
                label={selected.title ?? selected.asin}
                asin={selected.asin}
                sku={selected.sku ?? undefined}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="text-xs font-medium text-ink">{selected.title ?? selected.asin}</p>
                <p className="font-mono text-[10px] leading-[14px] text-muted-foreground">
                  ASIN: {selected.asin}
                  {selected.sku && !vendor ? ` | SKU: ${selected.sku}` : ""}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="xs"
                className="gap-1.5 rounded-[var(--paper-radius-control-compact)] px-2.5 font-medium"
                onClick={onReplace}
              >
                <IconRefresh />
                {t("products.single.replace")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="gap-1.5 rounded-[var(--paper-radius-control-compact)] px-2.5 font-medium text-text-secondary"
                onClick={selection.removeAll}
              >
                <IconX />
                {t("products.single.remove")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
            <span className="flex size-8 items-center justify-center">
              <IconBox className="size-6 text-muted-foreground" aria-hidden />
            </span>
            <p className="whitespace-pre-line text-xs leading-5 text-muted-foreground">
              {t("products.single.empty")}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function SingleChip({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-[18px] items-center rounded-[5px] border border-line bg-surface px-1.5 text-[10px] leading-3 text-text-secondary">
      {children}
    </span>
  );
}

/** Paper shows no pager; keep one only when there is more than one page. */
function SinglePanePagination({
  page,
  pagination,
  onPageChange,
}: {
  page: number;
  pagination?: { total: number; pageSize: number };
  onPageChange: (page: number) => void;
}) {
  if (!pagination || pagination.total <= pagination.pageSize) return null;
  return (
    <PaginationFooter
      page={page}
      total={pagination.total}
      pageSize={pagination.pageSize}
      onPageChange={onPageChange}
    />
  );
}
